import { Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { useDropzone } from 'react-dropzone';
import type { DocumentAsset } from '../../../services/admin/adminBackendServiceDocumentLibraryMixin';
import { DOCUMENT_LIBRARY_ACCEPT, titleFromDocumentFileName } from './documentLibraryAccept';
import MediaLibraryDocumentItem from './MediaLibraryDocumentItem';

export const PRIMARY_MAIN = 'primary.main';

export function DocumentsPdfDropZone({
  uploading,
  onUpload,
}: {
  uploading: boolean;
  onUpload: (file: File, title: string, description: string) => void;
}) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: DOCUMENT_LIBRARY_ACCEPT,
    multiple: false,
    disabled: uploading,
    onDrop: (accepted) => {
      const file = accepted[0];
      if (!file) return;
      void onUpload(file, titleFromDocumentFileName(file.name), '');
    },
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: '2px dashed',
        borderColor: isDragActive ? PRIMARY_MAIN : 'divider',
        borderRadius: 2,
        py: 3,
        textAlign: 'center',
        cursor: uploading ? 'default' : 'pointer',
        bgcolor: isDragActive ? 'action.selected' : 'action.hover',
        color: isDragActive ? PRIMARY_MAIN : 'text.secondary',
        transition: 'border-color 0.12s ease, background-color 0.12s ease',
        '&:hover': uploading ? undefined : { borderColor: PRIMARY_MAIN },
      }}
    >
      <input {...getInputProps()} />
      <Typography variant="caption">
        {uploading
          ? 'Uploading…'
          : isDragActive
            ? 'Drop document here…'
            : 'Drop a document or click to add'}
      </Typography>
    </Box>
  );
}

export function DocumentsLibraryList({
  loading,
  documents,
  isNarrow,
  isDeletable,
  deleteMessage,
  confirmId,
  deleting,
  deleteError,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
  onViewDocument,
}: {
  loading: boolean;
  documents: DocumentAsset[];
  isNarrow: boolean;
  isDeletable: (doc: DocumentAsset) => boolean;
  deleteMessage: (doc: DocumentAsset) => string;
  confirmId: string | null;
  deleting: boolean;
  deleteError: string | null;
  onRequestDelete: (id: string) => void;
  onCancelDelete: () => void;
  onConfirmDelete: (doc: DocumentAsset) => void;
  onViewDocument: (id: string) => void;
}) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (documents.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No documents yet — drop a document above to add it.
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isNarrow ? 'row' : 'column',
        gap: 1.5,
        minWidth: 0,
        overflowX: isNarrow ? 'auto' : 'hidden',
      }}
    >
      {documents.map((doc) => (
        <MediaLibraryDocumentItem
          key={doc.externalId}
          doc={doc}
          isNarrow={isNarrow}
          isDeletable={isDeletable(doc)}
          deleteMessage={deleteMessage(doc)}
          confirming={confirmId === doc.externalId}
          deleting={deleting}
          deleteError={deleteError}
          onRequestDelete={() => onRequestDelete(doc.externalId)}
          onCancelDelete={onCancelDelete}
          onConfirmDelete={() => onConfirmDelete(doc)}
          onViewDocument={onViewDocument}
        />
      ))}
    </Box>
  );
}
